import { useMemo, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { LoadOpenItemRow, LoadOpenItemsData } from "@silph-scope/shared";
import { useMe } from "../../api/hooks";
import { apiFetch } from "../../api/client";
import { usePresentMode } from "../../hooks/usePresentMode";
import { RosterAdminButton } from "../../components/RosterEditor";

export function LoadPageHeader({
  dashboardId,
  eyebrow,
  title,
  subtitle,
  badges = ["Admin only", "Rolling 12 months"],
  extra,
  syncedAt,
  interval,
  isFetching
}: {
  dashboardId: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  badges?: string[];
  extra?: ReactNode;
  syncedAt: Date | null;
  interval: string;
  isFetching: boolean;
}) {
  const { setPresent } = usePresentMode();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const isAdmin = me?.roles.includes("admin") ?? false;

  async function handleRefresh() {
    await apiFetch(`/api/dashboards/${dashboardId}/refresh`, { method: "POST" });
    await queryClient.refetchQueries({ queryKey: ["panel", dashboardId] });
  }

  return (
    <header className="page-header">
      <div>
        <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          Dashboards <i className="ri-arrow-right-s-line" /> {eyebrow}
        </div>
        <div className="page-title-row">
          <h1>{title}</h1>
          {badges.map((badge) => (
            <span key={badge} className="panel-badge">{badge}</span>
          ))}
          {extra}
        </div>
        <p>{subtitle}</p>
      </div>
      <div className="header-controls">
        <div className="chip">
          <span className="dot" />
          {syncedAt ? `Live · synced ${relativeTime(syncedAt)}` : "Awaiting first sync"}
        </div>
        <div className="chip">
          <i className="ri-refresh-line" />
          Refresh Rate: <strong>{interval}</strong>
        </div>
        <RosterAdminButton pageId={dashboardId} />
        {isAdmin && (
          <button className="btn-primary" type="button" onClick={handleRefresh} disabled={isFetching}>
            <i className="ri-restart-line" />
            Refresh now
          </button>
        )}
        <button className="btn-ghost" type="button" onClick={() => setPresent(true)}>
          <i className="ri-slideshow-line" />
          Present
        </button>
      </div>
    </header>
  );
}

export function KpiCell({
  label,
  value,
  sub,
  valueClassName
}: {
  label: string;
  value: number | string;
  sub?: string;
  valueClassName?: string;
}) {
  return (
    <div className="dt-kpi-cell">
      <div className="kpi-label">{label}</div>
      <div className={`dt-kpi-value${valueClassName ? ` ${valueClassName}` : ""}`}>
        {value}
        {sub && <span className="dt-kpi-sub"> {sub}</span>}
      </div>
    </div>
  );
}

export function LoadOpenItemsTable({
  data,
  selected,
  selectedKey,
  onClear
}: {
  data?: LoadOpenItemsData;
  selected: string | null;
  selectedKey: "assignee" | "partner";
  onClear: () => void;
}) {
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return selected ? all.filter((r) => r[selectedKey] === selected) : all;
  }, [data, selected, selectedKey]);

  return (
    <div className="card table-panel">
      <div className="panel-header">
        <h2>Open items</h2>
        <span className="dt-filter-note">
          {rows.length} item{rows.length === 1 ? "" : "s"}
          {selected && (
            <>
              {" "}
              · filtered to <strong>{selected}</strong>
              <button className="dt-filter-clear" type="button" onClick={onClear}>
                Clear
              </button>
            </>
          )}
        </span>
      </div>
      <div className="dt-detail-scroll">
        <div className="tl-open-header">
          <span>Task</span>
          <span>Assignee</span>
          <span>Partner</span>
          <span>Size</span>
          <span>Type</span>
          <span>Due</span>
          <span style={{ textAlign: "right" }}>Age</span>
          <span style={{ textAlign: "right" }}>Overdue</span>
          <span>Blocked</span>
          <span />
        </div>
        <div className="table-body">
          {rows.map((row) => (
            <LoadOpenItemRowView key={row.id} row={row} />
          ))}
        </div>
      </div>
    </div>
  );
}

function LoadOpenItemRowView({ row }: { row: LoadOpenItemRow }) {
  return (
    <div className="tl-open-row">
      <span className="task-title" title={row.title}>{row.title}</span>
      <span>{row.assignee}</span>
      <span>{row.partner}</span>
      <span>{row.size}</span>
      <span>{row.type}</span>
      <span className="mono">{row.due ? row.due.slice(5) : "—"}</span>
      <span className="num" style={{ textAlign: "right" }}>{row.ageDays}d</span>
      <span className={`num${row.overdueDays > 0 ? " is-bad" : ""}`} style={{ textAlign: "right" }}>
        {row.overdueDays > 0 ? `${row.overdueDays}d` : "—"}
      </span>
      <span>{row.blocked ? <span className="pill bad">Blocked</span> : "—"}</span>
      <a className="dt-detail-link" href={row.url} target="_blank" rel="noopener noreferrer" title="Open in Asana">
        <i className="ri-external-link-line" />
      </a>
    </div>
  );
}

export function relativeTime(date: Date): string {
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
